/* eslint-disable react/jsx-no-bind */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  AssignmentOutlined,
  ChevronLeft,
  ChevronRight,
  Close,
  KeyboardArrowDown,
  KeyboardArrowRight,
  PlayArrow,
  SaveAlt,
  SentimentVeryDissatisfied,
  ThumbDownOutlined,
  ThumbUpOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { saveAs } from 'file-saver';
import moment from 'moment-timezone';
import { AlertsContext } from '../shared/alerts/Alerts.context';
import LoggingService from '../shared/Logging.api';
import colors from '../colors.styles';
import SpinningSpark from '../shared/components/SpinningSpark';
import adminFormStyles from '../shared/styles/adminForm.styles';
import TasksApi from './Tasks.api';
import {
  getRequestSubtitleParts as getTaskSubtitleParts,
  getTaskStatus,
  TASK_STATUS,
} from './Tasks.service';
import { MEDIA_TYPES } from './Task.model';

const STATUS_CHIP = {
  [TASK_STATUS.ACTIVE]: {
    label: 'Active',
    background: colors.base.light_green,
    color: colors.base.dark_green,
  },
  [TASK_STATUS.COMPLETE]: {
    label: 'Complete',
    background: colors.base.very_light_gray,
    color: colors.base.dark_gray,
  },
};

const styles = {
  paper: {
    ...adminFormStyles.section,
    backgroundColor: colors.base.light,
    overflow: 'hidden',
  },
  taskHeader: {
    alignItems: 'flex-start',
    cursor: 'pointer',
    display: 'flex',
    gap: 1.5,
    px: 2,
    py: 2,
  },
  expandChevron: {
    color: '#656d80',
    mt: '2px',
  },
  statusDot: {
    backgroundColor: colors.base.dark_green,
    borderRadius: '50%',
    display: 'inline-block',
    height: 8,
    mr: 1,
    verticalAlign: 'middle',
    width: 8,
  },
  // Dot, title, and chip share one text flow so a long title wraps at a word
  // boundary instead of dropping below the dot as a flex item.
  taskTitle: {
    color: colors.base.bentonville_blue,
    fontSize: '16px',
    fontWeight: 800,
    lineHeight: 1.5,
    overflowWrap: 'anywhere',
  },
  chip: {
    fontSize: '12px',
    fontWeight: 700,
    height: 24,
    ml: 1,
    verticalAlign: 'middle',
  },
  taskSubtitle: {
    alignItems: 'center',
    color: '#656d80',
    display: 'flex',
    flexWrap: 'wrap',
    fontSize: '14px',
    gap: 0.75,
    mt: 0.5,
  },
  likedContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: 0.5,
  },
  likedIcon: {
    fontSize: 16,
  },
  taskDetailsButton: {
    ...adminFormStyles.secondaryButton,
    color: colors.base.bentonville_blue,
    flexShrink: 0,
    ml: 'auto',
  },
  taskBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    borderTop: '1px solid #e9ecee',
    px: 2,
    py: 1.5,
  },
  contentRequest: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  contentRequestHeader: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    gap: 1,
  },
  contentRequestTitle: {
    color: colors.base.bentonville_blue,
    fontSize: '14px',
    fontWeight: 700,
  },
  contentRequestSubmissionPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    padding: '20px 12px',
    borderRadius: '12px',
    border: '1px dashed #DBE2E6',
  },
  contentRequestSubmissionPlaceholderContent: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
  },
  contentRequestSubmissionPlaceholderIcon: {
    color: '#5D6C7B',
    width: '14px',
    height: '14px',
  },
  contentRequestSubmissionPlaceholderText: {
    color: '#5D6C7B',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  contentRequestSubmissions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  contentRequestSubmissionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
  },
  submission: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    border: '1px solid #DBE2E6',
    background: '#FFF',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  submissionThumbnailContainer: {
    position: 'relative',
    width: '100%',
    height: '140px',
  },
  submissionThumbnail: {
    display: 'flex',
    height: '100%',
    width: '100%',
    objectFit: 'cover',
  },
  playVideoCircle: {
    position: 'absolute',
    display: 'flex',
    width: '40px',
    height: '40px',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.4)',
    borderRadius: '100%',
  },
  playVideoTriangle: {
    color: '#FFF',
    fontSize: '28px',
  },
  loadMoreSubmissionsButton: {
    borderRadius: '8px',
    border: '1px solid #DBE2E6',
    textTransform: 'none',
  },
  submissionInfo: {
    display: 'flex',
    padding: '12px 16px',
    flexDirection: 'column',
    gap: '10px',
  },
  submissionInfoText: {
    display: 'flex',
    gap: '8px',
  },
  submissionUsername: {
    color: colors.base.bentonville_blue,
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 'normal',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  submissionStoreNumber: {
    color: colors.base.bentonville_blue,
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  submissionDate: {
    color: '#656d80',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 'normal',
    whiteSpace: 'nowrap',
  },
  submissionActions: {
    display: 'flex',
    height: '36px',
    gap: '8px',
  },
  submissionActionButton: {
    display: 'flex',
    minWidth: 0,
    flex: '1 0 0',
    borderRadius: '8px',
    border: '1px solid #DBE2E6',
  },
  submissionActionButtonIcon: {
    color: '#656D80',
    width: '18px',
    height: '18px',
  },
  submissionOverlayBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: 99,
  },
  submissionOverlay: {
    position: 'fixed',
    display: 'grid',
    gridTemplateRows: '1fr',
    gridTemplateColumns: 'repeat(3, 1fr)',
    width: '1002px', // this needs to be 1002 instead of 1000 so that we don't have sub-pixel rounding issues (because this container gets divided into 3)
    height: '500px',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '12px',
    overflow: 'hidden',
    background: '#FFF',
    zIndex: 100,
  },
  submissionMediaContainer: {
    position: 'relative',
    display: 'flex',
    gridRow: 'span 1',
    gridColumn: 'span 2',
    alignItems: 'flex-start',
    padding: '12px',
    background: '#000',
  },
  submissionMedia: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  submissionCount: {
    position: 'relative',
    padding: '4px 8px',
    borderRadius: '16px',
    background: 'rgba(0, 0, 0, 0.40)',
  },
  submissionCountText: {
    color: '#FFF',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '14px',
  },
  submissionOverlayChevron: {
    position: 'absolute',
    minWidth: 0,
    padding: '7px',
    top: '50%',
    transform: 'translateY(-50%)',
    borderRadius: '100%',
    background: '#FFF',
    boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.10)',
    '&:hover': { background: '#F5F5F5D6' },
  },
  submissionOverlayChevronIcon: {
    width: '18px',
    height: '18px',
  },
  submissionDetails: {
    display: 'flex',
    gridRow: 'span 1',
    gridColumn: 'span 1',
    flexDirection: 'column',
    gap: '10px',
    padding: '24px',
  },
  submissionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  submissionTaskTitle: {
    color: colors.base.bentonville_blue,
    fontSize: '16px',
    fontWeight: 700,
  },
  submissionContentRequestTitle: {
    color: colors.base.bentonville_blue,
    fontSize: '14px',
    fontWeight: 500,
  },
  submissionComment: {
    color: '#5D6C7B',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  submissionDetailsBox: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px',
    gap: '8px',
    borderRadius: '12px',
    border: '1px solid #E9ECEE',
    background: '#F6F6F6',
  },
  submissionDetailsBoxItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
  },
  submissionDetailsBoxItemLabel: {
    color: '#656D80',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  submissionDetailsBoxItemValue: {
    color: colors.base.bentonville_blue,
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 'normal',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  submissionOverlayActions: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 0',
    justifyContent: 'flex-end',
    gap: '10px',
  },
  submissionOverlayReviewActions: {
    display: 'flex',
    gap: '8px',
  },
  submissionOverlayActionButton: {
    display: 'flex',
    minWidth: 0,
    width: '100%',
    padding: '8px 16px',
    gap: '10px',
    borderRadius: '8px',
    border: '1px solid #DBE2E6',
    textTransform: 'none',
  },
  submissionOverlayActionButtonIcon: {
    color: '#656D80',
    width: '18px',
    height: '18px',
  },
  submissionOverlayActionButtonText: {
    color: '#656D80',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  submissionOverlayCloseButton: {
    position: 'absolute',
    minWidth: 0,
    width: '18px',
    height: '18px',
    right: '16px',
    top: '16px',
  },
};

const submissionShape = PropTypes.shape({
  media_url: PropTypes.string,
  store_number: PropTypes.number,
  comment: PropTypes.string,
  user_name: PropTypes.string,
  walmart_id: PropTypes.string,
  updatedAt: PropTypes.string,
});

const contentRequestShape = PropTypes.shape({
  id: PropTypes.number,
  ui_id: PropTypes.string,
  title: PropTypes.string,
  media_type: PropTypes.string,
  submissions: PropTypes.arrayOf(submissionShape),
  hasMoreSubmissions: PropTypes.bool,
});

const taskShape = PropTypes.shape({
  task_id: PropTypes.number.isRequired,
  title: PropTypes.string,
  launch_date: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  due_date: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  submission_count: PropTypes.number,
  liked_count: PropTypes.number,
  content_requests: PropTypes.arrayOf(contentRequestShape),
});

function ExpandChevron({ expanded, label }) {
  return (
    <IconButton
      aria-expanded={expanded}
      aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label}`}
      size="small"
      sx={styles.expandChevron}
    >
      {expanded ? (
        <KeyboardArrowDown fontSize="small" />
      ) : (
        <KeyboardArrowRight fontSize="small" />
      )}
    </IconButton>
  );
}

ExpandChevron.propTypes = {
  expanded: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
};

export default function Task({ task }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);
  const status = getTaskStatus(task);
  const chip = STATUS_CHIP[status];
  const title = task.title || 'Untitled request';
  const subtitle = getTaskSubtitleParts(task);
  const contentRequests = task.content_requests || [];

  function openDetails(event) {
    event.stopPropagation();
    navigate(`/tasks/${task.task_id}`);
  }

  return (
    <Paper sx={styles.paper} variant="outlined">
      <Box
        onClick={() => setExpanded((current) => !current)}
        sx={styles.taskHeader}
      >
        <ExpandChevron expanded={expanded} label={title} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="div" sx={styles.taskTitle}>
            {status === TASK_STATUS.ACTIVE && (
              <Box component="span" sx={styles.statusDot} />
            )}
            {title}
            {chip && (
              <Chip
                label={chip.label}
                size="small"
                sx={{
                  ...styles.chip,
                  backgroundColor: chip.background,
                  color: chip.color,
                }}
              />
            )}
          </Typography>
          <Typography component="div" sx={styles.taskSubtitle}>
            <span>{subtitle.submissions}</span>
            <span>·</span>
            <Box sx={styles.likedContainer}>
              <ThumbUpOutlined
                aria-label="Liked submissions"
                sx={styles.likedIcon}
              />
              {subtitle.liked}
            </Box>
            {subtitle.due && (
              <>
                <span>·</span>
                <span>{subtitle.due}</span>
              </>
            )}
          </Typography>
        </Box>
        <Button
          onClick={openDetails}
          startIcon={<AssignmentOutlined />}
          sx={styles.taskDetailsButton}
          variant="outlined"
        >
          View request details
        </Button>
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={styles.taskBody}>
          {contentRequests.map((contentRequest) => (
            <ContentRequest
              key={contentRequest.ui_id || contentRequest.id}
              taskTitle={task.title}
              contentRequest={contentRequest}
            />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}

Task.propTypes = {
  task: taskShape.isRequired,
};

function ContentRequest({ taskTitle, contentRequest }) {
  const [expanded, setExpanded] = useState(true);
  const [contentRequestWithSubmissions, setContentRequestWithSubmissions] =
    useState(contentRequest);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const selectedSubmissionRef = React.useRef(selectedSubmission);
  const [submissionPage, setSubmissionPage] = useState(1);
  const [isLoadingMoreSubmissions, setIsLoadingMoreSubmissions] =
    useState(false);
  const [
    shouldAutoAdvanceSubmissionOverlay,
    setShouldAutoAdvanceSubmissionOverlay,
  ] = useState(false);
  const [isDownloadingSubmission, setIsDownloadingSubmission] = useState(false);
  const [downloadedSubmission, setDownloadedSubmission] = useState(null);

  const { showAlert } = useContext(AlertsContext);

  useEffect(() => {
    selectedSubmissionRef.current = selectedSubmission;
  }, [selectedSubmission]);

  useEffect(() => {
    const fetchSubmissions = async () => {
      setIsLoadingMoreSubmissions(true);
      const results = await TasksApi.getSubmissionsForContentRequest(
        contentRequest.id,
        submissionPage
      );
      if (
        submissionPage > 1 &&
        contentRequestWithSubmissions.submissions.indexOf(
          selectedSubmissionRef.current
        ) ===
          contentRequestWithSubmissions.submissions.length - 1
      ) {
        setShouldAutoAdvanceSubmissionOverlay(true);
      }
      setContentRequestWithSubmissions((current) => ({
        ...current,
        submissions: current.submissions.concat(results.submissions),
        hasMoreSubmissions: results.has_more,
      }));
      setIsLoadingMoreSubmissions(false);
    };
    fetchSubmissions();
  }, [submissionPage]);

  async function downloadSelectedSubmission(submission, fileName) {
    try {
      setIsDownloadingSubmission(true);
      setDownloadedSubmission(submission);
      const response = await fetch(submission.media_url);
      const mediaBlob = await response.blob();
      saveAs(mediaBlob, fileName);
    } catch (error) {
      LoggingService.error('Failed to download submission media', error);
      showAlert('Failed to download submission media. Please try again.');
    } finally {
      setIsDownloadingSubmission(false);
    }
  }

  return (
    <>
      <SubmissionOverlay
        open={selectedSubmission !== null}
        selectedTaskTitle={taskTitle}
        selectedContentRequest={contentRequestWithSubmissions}
        selectedSubmission={selectedSubmission}
        setSelectedSubmission={setSelectedSubmission}
        downloadSelectedSubmission={downloadSelectedSubmission}
        isDownloadingSubmission={isDownloadingSubmission}
        downloadedSubmission={downloadedSubmission}
        setSubmissionPage={setSubmissionPage}
        hasMoreSubmissions={contentRequestWithSubmissions.hasMoreSubmissions}
        isLoadingMoreSubmissions={isLoadingMoreSubmissions}
        shouldAutoAdvanceSubmissionOverlay={shouldAutoAdvanceSubmissionOverlay}
        setShouldAutoAdvanceSubmissionOverlay={
          setShouldAutoAdvanceSubmissionOverlay
        }
      />
      <Box sx={styles.contentRequest}>
        <Box
          onClick={() => setExpanded((current) => !current)}
          sx={styles.contentRequestHeader}
        >
          <ExpandChevron
            expanded={expanded}
            label={contentRequestWithSubmissions.title}
          />
          <Typography sx={styles.contentRequestTitle}>
            {contentRequestWithSubmissions.title}
          </Typography>
        </Box>
        <Collapse in={expanded} unmountOnExit>
          {contentRequestWithSubmissions.submissions.length === 0 ? (
            <Box sx={styles.contentRequestSubmissionPlaceholder}>
              <Box sx={styles.contentRequestSubmissionPlaceholderContent}>
                <SentimentVeryDissatisfied
                  sx={styles.contentRequestSubmissionPlaceholderIcon}
                />

                <Typography sx={styles.contentRequestSubmissionPlaceholderText}>
                  There&apos;s nothing here yet!
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box style={styles.contentRequestSubmissions}>
              <div style={styles.contentRequestSubmissionGrid}>
                {contentRequestWithSubmissions.submissions.map((submission) => (
                  <Submission
                    key={`${submission.media_url}-${submission.comment}-${submission.updatedAt}`}
                    taskTitle={taskTitle}
                    contentRequest={contentRequestWithSubmissions}
                    submission={submission}
                    setSelectedSubmission={setSelectedSubmission}
                    downloadSelectedSubmission={downloadSelectedSubmission}
                    isDownloadingSubmission={isDownloadingSubmission}
                    downloadedSubmission={downloadedSubmission}
                  />
                ))}
              </div>
              {contentRequestWithSubmissions.hasMoreSubmissions && (
                <Button
                  sx={styles.loadMoreSubmissionsButton}
                  disabled={isLoadingMoreSubmissions}
                  onClick={() => {
                    setSubmissionPage((current) => current + 1);
                  }}
                >
                  {isLoadingMoreSubmissions ? (
                    <SpinningSpark height="25" marginTop="0" />
                  ) : (
                    'LOAD MORE'
                  )}
                </Button>
              )}
            </Box>
          )}
        </Collapse>
      </Box>
    </>
  );
}

ContentRequest.propTypes = {
  taskTitle: PropTypes.string.isRequired,
  contentRequest: contentRequestShape.isRequired,
};

function Submission({
  taskTitle,
  contentRequest,
  submission,
  setSelectedSubmission,
  downloadSelectedSubmission,
  isDownloadingSubmission,
  downloadedSubmission,
}) {
  const [firstName, lastName] = submission.user_name.split(' ');

  return (
    <Box
      sx={styles.submission}
      onClick={() => {
        setSelectedSubmission(submission);
      }}
    >
      <Box sx={styles.submissionThumbnailContainer}>
        {contentRequest.media_type === MEDIA_TYPES.VIDEO ? (
          <>
            {/* This is a slightly hacky way to generate a video thumbnail */}
            <video
              muted
              preload="metadata"
              src={`${submission.media_url}#t=0.1`}
              style={styles.submissionThumbnail}
            />

            <Box sx={styles.playVideoCircle}>
              <PlayArrow sx={styles.playVideoTriangle} />
            </Box>
          </>
        ) : (
          <img
            src={submission.media_url}
            alt={submission.store_number}
            style={styles.submissionThumbnail}
          />
        )}
      </Box>

      <Box sx={styles.submissionInfo}>
        <Box sx={styles.submissionInfoText}>
          <Typography sx={styles.submissionUsername}>
            {firstName}
            {lastName && <> {lastName.charAt(0)}.</>}
          </Typography>

          <Typography sx={styles.submissionStoreNumber}>
            #{submission.store_number}
          </Typography>

          <Typography sx={styles.submissionDate}>
            {moment(submission.updatedAt).format('MMM D')}
          </Typography>
        </Box>

        <Box sx={styles.submissionActions}>
          <Button
            onClick={(event) => event.stopPropagation()}
            sx={styles.submissionActionButton}
          >
            <ThumbUpOutlined sx={styles.submissionActionButtonIcon} />
          </Button>

          <Button
            onClick={(event) => event.stopPropagation()}
            sx={styles.submissionActionButton}
          >
            <ThumbDownOutlined sx={styles.submissionActionButtonIcon} />
          </Button>

          <Button
            onClick={(event) => {
              event.stopPropagation();
              downloadSelectedSubmission(
                submission,
                `${taskTitle} - ${contentRequest.title} - ${submission.store_number}`
              );
            }}
            sx={styles.submissionActionButton}
          >
            {isDownloadingSubmission && downloadedSubmission === submission ? (
              <SpinningSpark height="18" marginTop="0" />
            ) : (
              <SaveAlt sx={styles.submissionActionButtonIcon} />
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

Submission.propTypes = {
  taskTitle: PropTypes.string.isRequired,
  contentRequest: contentRequestShape.isRequired,
  submission: submissionShape.isRequired,
  setSelectedSubmission: PropTypes.func.isRequired,
  downloadSelectedSubmission: PropTypes.func.isRequired,
  isDownloadingSubmission: PropTypes.bool.isRequired,
  downloadedSubmission: submissionShape.isRequired,
};

function SubmissionOverlay({
  open,
  selectedTaskTitle,
  selectedContentRequest,
  selectedSubmission,
  setSelectedSubmission,
  downloadSelectedSubmission,
  isDownloadingSubmission,
  downloadedSubmission,
  setSubmissionPage,
  hasMoreSubmissions,
  isLoadingMoreSubmissions,
  shouldAutoAdvanceSubmissionOverlay,
  setShouldAutoAdvanceSubmissionOverlay,
}) {
  const currentSubmissionIndex =
    selectedContentRequest.submissions.indexOf(selectedSubmission);
  const finalSubmissionIndex = selectedContentRequest.submissions.length - 1;

  useEffect(() => {
    if (open && shouldAutoAdvanceSubmissionOverlay) {
      setSelectedSubmission(
        selectedContentRequest.submissions[currentSubmissionIndex + 1]
      );
      setShouldAutoAdvanceSubmissionOverlay(false);
    }
  }, [shouldAutoAdvanceSubmissionOverlay]);

  return (
    <>
      {open && (
        <>
          <Box
            onClick={() => setSelectedSubmission(null)}
            sx={styles.submissionOverlayBackdrop}
          />

          <Box sx={styles.submissionOverlay}>
            <Box sx={styles.submissionMediaContainer}>
              {selectedContentRequest.media_type === MEDIA_TYPES.PHOTO && (
                <img
                  src={selectedSubmission.media_url}
                  alt={selectedSubmission.store_number}
                  style={styles.submissionMedia}
                />
              )}

              {selectedContentRequest.media_type === MEDIA_TYPES.VIDEO && (
                <ReactPlayer
                  url={selectedSubmission.media_url}
                  controls
                  width="100%"
                  height="100%"
                  style={styles.submissionMedia}
                />
              )}

              {currentSubmissionIndex > 0 && (
                <Button
                  sx={{ ...styles.submissionOverlayChevron, left: '12px' }}
                  onClick={() => {
                    setSelectedSubmission(
                      selectedContentRequest.submissions[
                        Math.max(currentSubmissionIndex - 1, 0)
                      ]
                    );
                  }}
                >
                  <ChevronLeft sx={styles.submissionOverlayChevronIcon} />
                </Button>
              )}

              {(hasMoreSubmissions ||
                currentSubmissionIndex < finalSubmissionIndex) && (
                <Button
                  sx={{ ...styles.submissionOverlayChevron, right: '12px' }}
                  disabled={
                    isLoadingMoreSubmissions &&
                    currentSubmissionIndex === finalSubmissionIndex
                  }
                  onClick={() => {
                    if (currentSubmissionIndex < finalSubmissionIndex) {
                      setSelectedSubmission(
                        selectedContentRequest.submissions[
                          Math.min(
                            currentSubmissionIndex + 1,
                            finalSubmissionIndex
                          )
                        ]
                      );
                    } else {
                      setSubmissionPage((current) => current + 1);
                    }
                  }}
                >
                  {isLoadingMoreSubmissions &&
                  currentSubmissionIndex === finalSubmissionIndex ? (
                    <SpinningSpark height="18" width="18" marginTop="0" />
                  ) : (
                    <ChevronRight sx={styles.submissionOverlayChevronIcon} />
                  )}
                </Button>
              )}

              <Box sx={styles.submissionCount}>
                <Typography sx={styles.submissionCountText}>
                  {currentSubmissionIndex + 1} of{' '}
                  {selectedContentRequest.submissions.length}
                </Typography>
              </Box>
            </Box>

            <Box sx={styles.submissionDetails}>
              <Box sx={styles.submissionHeader}>
                <Typography sx={styles.submissionTaskTitle}>
                  {selectedTaskTitle}
                </Typography>

                <Typography sx={styles.submissionContentRequestTitle}>
                  {selectedContentRequest.title}
                </Typography>
              </Box>

              <Typography sx={styles.submissionComment}>
                {selectedSubmission.comment}
              </Typography>

              <Box sx={styles.submissionDetailsBox}>
                <Box sx={styles.submissionDetailsBoxItem}>
                  <Typography sx={styles.submissionDetailsBoxItemLabel}>
                    Associate
                  </Typography>

                  <Typography sx={styles.submissionDetailsBoxItemValue}>
                    <span style={{ fontWeight: 700 }}>
                      {selectedSubmission.user_name}
                    </span>{' '}
                    ({selectedSubmission.walmart_id})
                  </Typography>
                </Box>

                <Box sx={styles.submissionDetailsBoxItem}>
                  <Typography sx={styles.submissionDetailsBoxItemLabel}>
                    Store
                  </Typography>

                  <Typography sx={styles.submissionDetailsBoxItemValue}>
                    #{selectedSubmission.store_number}
                  </Typography>
                </Box>

                <Box sx={styles.submissionDetailsBoxItem}>
                  <Typography sx={styles.submissionDetailsBoxItemLabel}>
                    Submitted
                  </Typography>

                  <Typography sx={styles.submissionDetailsBoxItemValue}>
                    {moment(selectedSubmission.updatedAt).format('MMM D')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={styles.submissionOverlayActions}>
                <Box sx={styles.submissionOverlayReviewActions}>
                  <Button sx={styles.submissionOverlayActionButton}>
                    <ThumbUpOutlined
                      sx={styles.submissionOverlayActionButtonIcon}
                    />

                    <Typography sx={styles.submissionOverlayActionButtonText}>
                      Love this!
                    </Typography>
                  </Button>
                  <Button sx={styles.submissionOverlayActionButton}>
                    <ThumbDownOutlined
                      sx={styles.submissionOverlayActionButtonIcon}
                    />

                    <Typography sx={styles.submissionOverlayActionButtonText}>
                      Can&apos;t use
                    </Typography>
                  </Button>
                </Box>
                <Button
                  onClick={() =>
                    downloadSelectedSubmission(
                      selectedSubmission,
                      `${selectedTaskTitle} - ${selectedContentRequest.title} - ${selectedSubmission.store_number}`
                    )
                  }
                  sx={styles.submissionOverlayActionButton}
                >
                  {isDownloadingSubmission &&
                  downloadedSubmission === selectedSubmission ? (
                    <SpinningSpark height="18" marginTop="0" />
                  ) : (
                    <>
                      <SaveAlt sx={styles.submissionOverlayActionButtonIcon} />

                      <Typography sx={styles.submissionOverlayActionButtonText}>
                        Download content
                      </Typography>
                    </>
                  )}
                </Button>
              </Box>

              <Button
                sx={styles.submissionOverlayCloseButton}
                onClick={() => setSelectedSubmission(null)}
              >
                <Close />
              </Button>
            </Box>
          </Box>
        </>
      )}
    </>
  );
}

SubmissionOverlay.propTypes = {
  open: PropTypes.bool.isRequired,
  selectedTaskTitle: PropTypes.string.isRequired,
  selectedContentRequest: contentRequestShape.isRequired,
  selectedSubmission: submissionShape,
  setSelectedSubmission: PropTypes.func.isRequired,
  downloadSelectedSubmission: PropTypes.func.isRequired,
  isDownloadingSubmission: PropTypes.bool.isRequired,
  downloadedSubmission: submissionShape.isRequired,
  setSubmissionPage: PropTypes.func.isRequired,
  hasMoreSubmissions: PropTypes.bool,
  isLoadingMoreSubmissions: PropTypes.bool.isRequired,
  shouldAutoAdvanceSubmissionOverlay: PropTypes.bool.isRequired,
  setShouldAutoAdvanceSubmissionOverlay: PropTypes.func.isRequired,
};

SubmissionOverlay.defaultProps = {
  selectedSubmission: null,
  hasMoreSubmissions: false,
};
